import { ArrowRight, Star } from "lucide-react";
import { motion, useReducedMotion } from "motion/react";
import { useEffect, useRef, useState } from "react";

import { PrizeGamesChips } from "@/components/campaigns/prize-games-chips";
import { cn } from "@/lib/utils";
import { useTranslation } from "react-i18next";

/** Reel geometry (Figma 4588:39234: 193×118 cells, 12 gap, 154-tall rail) —
 *  cells are fixed-size so the landing offset is exact. */
const CELL_W = 193;
const CELL_GAP = 12;
const STRIDE = CELL_W + CELL_GAP;
const CELL_H = 118;
const RAIL_H = 154;
/** The rail bleeds past the card edge on both sides (Figma: 670 vs 614). */
const RAIL_BLEED = 28;
/** How many times the prize list repeats in the strip. Must stay above
 *  SPIN_LOOPS_MAX + 1 so the landing index always exists in the strip. */
const REPEATS = 7;

// --- Reel feel (hand-tunable) -----------------------------------------------
// The spin plan is randomised inside these ranges so the stop can't be
// anticipated. Approx. total spin = (loops × prize count) / cells-per-second.
/** Full prize-list loops the reel travels before landing. */
const SPIN_LOOPS_MIN = 3;
const SPIN_LOOPS_MAX = 4;
/** Reel speed in cells/second — higher = faster scroll. */
const CELLS_PER_SECOND_MIN = 4;
const CELLS_PER_SECOND_MAX = 5.5;
/** Never spin shorter than this (ms), so a tiny prize list still reads. */
const MIN_SPIN_MS = 400;
/** Spin easing: launches fast, covers most of the distance early, then
 *  decelerates into a slow crawl before the stop. First pair = launch punch
 *  (smaller x / bigger y = punchier), second pair shapes the tail. */
const SPIN_EASE = "cubic-bezier(0.12, 0.65, 0.25, 1)";
/** Duration multiplier giving the slow tail room to breathe (1 = the old
 *  constant-speed timing; higher = longer crawl at the end). */
const SPIN_TAIL_FACTOR = 1.35;

export interface PrizeRevealWindowProps {
  /** All prize labels — the reel's content. */
  prizes: string[];
  /** The won prize — the reel lands on it (appended if not in `prizes`). */
  wonPrize: string;
  /** "You have N Prizes" caption — the campaign's queued unrevealed total. */
  pendingCount?: number;
  /** Games the win applies to (free spins) — the full list from the disclosure, shown under the
   *  result, never inside the prize cell (which shows the prize label alone). */
  games?: string[];
  onConfirm: () => void;
  /** Skip the rest of the run: settle this prize and claim everything still queued at once.
   *  Omitted when there is too little left to be worth offering — the surfaces own that rule,
   *  so this window only decides where the action sits. */
  onRevealAll?: () => void;
}

/** Burst spark end-offsets from the winner cell's centre — an elliptical
 *  spread (flatter vertically, the reel rail clips above/below). */
const SPARK_DIRECTIONS: readonly [number, number][] = [
  [126, 0],
  [90, 50],
  [-90, 50],
  [-126, 0],
  [-90, -50],
  [90, -50],
  [0, -64],
  [0, 64],
];

/** The "You won {{prize}}" sentence with the prize itself in gold. i18next
 *  inserts the interpolated value verbatim, so splitting on its first
 *  occurrence is safe in any language; if a translation somehow drops the
 *  placeholder, the sentence renders plain. */
const WonSentence = ({
  sentence,
  prize,
}: {
  sentence: string;
  prize: string;
}) => {
  const at = sentence.indexOf(prize);
  if (at === -1) return <>{sentence}</>;
  return (
    <>
      {sentence.slice(0, at)}
      <span className="text-prizes">{prize}</span>
      {sentence.slice(at + prize.length)}
    </>
  );
};

/**
 * Prize reveal window (Figma 4588:39234), opened from the in-game button's
 * prize-reveal state. A horizontal prize reel spins at a constant speed for a
 * randomised distance/time (so the landing can't be anticipated), then stops
 * dead on the won prize — the momentum jolts the whole card (brake wobble);
 * the result + confirm button spring in once it stops. Reduced motion skips
 * the spin.
 *
 * A campaign with a single prize type has no reel at all — there is nothing to
 * choose between, so the card opens straight on the result.
 */
export const PrizeRevealWindow = ({
  prizes,
  wonPrize,
  pendingCount = 1,
  games,
  onConfirm,
  onRevealAll,
}: PrizeRevealWindowProps) => {
  const { t } = useTranslation();
  const reduceMotion = useReducedMotion();
  const confirmRef = useRef<HTMLButtonElement>(null);
  // With a single possible prize the reel is fake drama — it isn't rendered at
  // all, and the card opens on the result (same landed state reduced motion
  // arrives at, which still keeps its reel: there the other prizes inform).
  const singlePrize = new Set([...prizes, wonPrize]).size <= 1;
  const skipSpin = !!reduceMotion || singlePrize;

  // Freeze the randomised reel plan on mount. The reveal is a one-shot ceremony
  // for a fixed win, but the parent rebuilds `prizes`/`wonPrize` with fresh
  // array/object identity on every render (registry `.map(...)`/spread). A memo
  // keyed on those would re-roll `targetIndex` on each re-render, leaving the
  // winner highlight on a different cell than the reel physically stops on — the
  // golden border/shine then land off-pointer (border "disappears"). A lazy
  // initializer runs exactly once, so the plan can't drift after mount.
  const [{ strip, startIndex, targetIndex, spinMs }] = useState(() => {
    const list = prizes.length > 0 ? [...prizes] : [];
    if (!list.includes(wonPrize)) list.push(wonPrize);
    const wonIndex = list.indexOf(wonPrize);
    // Randomise how far the reel travels (whole sequences, so it still lands
    // on the winner) and its constant speed — the total spin time varies run
    // to run, so the stop can't be anticipated. Ranges live in the tunables
    // block above.
    const repeat =
      SPIN_LOOPS_MIN +
      Math.floor(Math.random() * (SPIN_LOOPS_MAX - SPIN_LOOPS_MIN + 1));
    const cellsPerSecond =
      CELLS_PER_SECOND_MIN +
      Math.random() * (CELLS_PER_SECOND_MAX - CELLS_PER_SECOND_MIN);
    const targetIndex = repeat * list.length + wonIndex;
    const spinCells = targetIndex - wonIndex;
    return {
      strip: Array.from({ length: REPEATS }, () => list).flat(),
      // Start on the same in-list position (first repeat) so the travelled
      // distance is an exact multiple of the sequence → seamless loop.
      startIndex: wonIndex,
      targetIndex,
      spinMs: Math.max(
        MIN_SPIN_MS,
        Math.round((spinCells / cellsPerSecond) * 1000 * SPIN_TAIL_FACTOR)
      ),
    };
  });

  /** translateX that centres cell `i` under the pointer (strip is at left:50%). */
  const centerOn = (i: number): number => -(i * STRIDE + CELL_W / 2);

  const [landed, setLanded] = useState(skipSpin);
  const [x, setX] = useState(() =>
    centerOn(skipSpin ? targetIndex : startIndex)
  );
  const [transition, setTransition] = useState<string>();

  useEffect(() => {
    if (skipSpin) return;
    // Kick the spin on the next tick: one eased transition onto the winner —
    // fast launch, then a decelerating crawl into the stop (SPIN_EASE above).
    // Land via transitionend with a timeout fallback (transitionend can be
    // missed in hidden tabs).
    const kick = window.setTimeout(() => {
      setTransition(`transform ${spinMs}ms ${SPIN_EASE}`);
      setX(centerOn(targetIndex));
    }, 60);
    const landFallback = window.setTimeout(
      () => setLanded(true),
      60 + spinMs + 250
    );
    return () => {
      window.clearTimeout(kick);
      window.clearTimeout(landFallback);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (landed) confirmRef.current?.focus();
  }, [landed]);

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={t("igwButton.youHaveWon", "You have won!")}
      // `pointer-events-auto` is required, not cosmetic: the widget's shadow
      // host sets `pointer-events: none` and every interactive layer opts back
      // in (see ui/dialog.tsx). Without it the confirm button renders but no
      // click ever lands on it — clicks fall through to the page behind.
      className="pointer-events-auto fixed inset-0 z-10010 flex items-center justify-center p-4"
    >
      {/* Backdrop: dark veil + twinkling stars (decorative). The rays mount at
          the decision moment — they pop in as the reel lands, then spin. */}
      <div aria-hidden className="absolute inset-0 overflow-hidden bg-black/75">
        {/* Rays/stars are gated on !reduceMotion in JS: their `animation` is set
            inline, which overrides `motion-reduce:animate-none`, so the class
            alone wouldn't stop them. 150vmax is the smallest circle that still
            covers the viewport corners from this centre — 240vmax rasterised a
            layer big enough to exceed the GPU texture limit on large screens,
            forcing per-frame re-raster of a continuously-spinning gradient. */}
        {landed && !reduceMotion && (
          <div
            className="absolute top-[42%] left-1/2 size-[150vmax] -translate-x-1/2 -translate-y-1/2 rounded-full opacity-80 will-change-transform"
            style={{
              background:
                "repeating-conic-gradient(from 0deg at 50% 50%, color-mix(in srgb, var(--primary) 12%, transparent) 0deg 8deg, transparent 8deg 24deg)",
              animation:
                "reveal-rays-in 700ms cubic-bezier(0.2, 0.9, 0.3, 1.15) both, reveal-rays-spin 48s linear infinite",
            }}
          />
        )}
        {!reduceMotion &&
          (
            [
              ["top-[16%] left-[22%] size-7 -rotate-12 text-primary/50", 0],
              ["top-[12%] right-[20%] size-9 rotate-12 text-primary/40", 600],
              ["bottom-[18%] left-[16%] size-5 rotate-45 text-prizes/40", 1100],
              [
                "right-[24%] bottom-[12%] size-8 -rotate-6 text-primary/45",
                350,
              ],
            ] as const
          ).map(([classes, delay]) => (
            <Star
              key={classes}
              className={cn("absolute fill-current", classes)}
              style={{
                animation: `reveal-star-twinkle 2.4s ease-in-out ${delay}ms infinite`,
              }}
            />
          ))}
      </div>

      <div
        className="notification-shadow relative w-[min(94vw,614px)] animate-in rounded-2xl bg-background pt-12 pb-8 duration-300 zoom-in-90 fade-in motion-reduce:animate-none"
        // Brake wobble: the reel's momentum jolts the whole card when it
        // stops dead (overrides the finished enter animation). No reel, no
        // momentum — the single-prize card just pops in.
        style={
          landed && !reduceMotion && !singlePrize
            ? {
                animation:
                  "reveal-brake 440ms cubic-bezier(0.2, 0.9, 0.3, 1) both",
              }
            : undefined
        }
      >
        {/* "You have won!" banner overlapping the card's top edge. */}
        <span
          className="absolute -top-8 left-1/2 -rotate-2 rounded-full bg-prizes px-6 py-3 font-heading text-[22px] leading-none font-extrabold whitespace-nowrap text-background shadow-md"
          style={{
            transform: "translateX(-50%)",
          }}
        >
          {t("igwButton.youHaveWon", "You have won!")}
        </span>

        <p className="text-center font-body text-[13px] text-foreground-secondary">
          {t("prizeReveal.youHavePrizes", "You have {{count}} Prizes", {
            count: pendingCount,
          })}
        </p>
        {/* Pointer at the landing position. */}
        {!singlePrize && (
          <div className="mx-auto mt-2 h-0 w-0 border-x-[14px] border-t-[16px] border-x-transparent border-t-primary" />
        )}

        {/* Prize reel — the rail is a touch wider than the card (Figma), with
            edge fades so the passing prizes read toward the centre. Dropped
            entirely when the campaign has a single prize type: a rail of
            identical cells with nothing to choose between is noise, so the
            card goes straight to the result. */}
        {!singlePrize && (
          <div
            className="relative mt-1.5 overflow-hidden rounded-(--border-radius) bg-background-card"
            style={{
              height: RAIL_H,
              width: `calc(100% + ${RAIL_BLEED * 2}px)`,
              marginLeft: -RAIL_BLEED,
            }}
          >
            <div
              className="absolute left-1/2 flex"
              style={{
                gap: CELL_GAP,
                top: (RAIL_H - CELL_H) / 2,
                transform: `translateX(${x}px)`,
                transition,
              }}
              onTransitionEnd={(e) => {
                if (e.target === e.currentTarget) setLanded(true);
              }}
            >
              {strip.map((label, i) => {
                const isWinner = landed && i === targetIndex;
                return (
                  <div
                    key={i}
                    className={cn(
                      // Ticket look: soft vertical gradient + inset ring, with a
                      // prizes-tinted gift icon above the label.
                      "relative flex shrink-0 flex-col items-center justify-center gap-1.5 overflow-hidden rounded-(--border-radius-inner) border bg-gradient-to-b from-background-card-surface to-background px-3 text-center ring-1 ring-foreground/5 ring-inset",
                      isWinner
                        ? // Pop + glow when the reel lands on it.
                          "animate-in border-2 border-prizes text-prizes shadow-[0_0_20px_2px] shadow-prizes/40 duration-500 zoom-in-75 motion-reduce:animate-none"
                        : "border-border text-foreground"
                    )}
                    style={{ width: CELL_W, height: CELL_H }}
                  >
                    <span className="font-heading text-[18px] font-bold">
                      {label}
                    </span>
                    {/* Decision flourish: shine sweep + ring/spark burst, one-shot. */}
                    {isWinner && !reduceMotion && (
                      <span
                        aria-hidden
                        className="pointer-events-none absolute inset-0"
                      >
                        <span className="absolute inset-0 overflow-hidden rounded-[inherit]">
                          <span
                            className="absolute inset-y-0 left-0 w-1/3 -skew-x-12 bg-foreground/40 blur-[2px]"
                            style={{
                              animation:
                                "reveal-shine 600ms ease-out 150ms both",
                            }}
                          />
                        </span>
                        <span
                          className="absolute inset-0 rounded-[inherit] border-2 border-prizes"
                          style={{
                            animation:
                              "reveal-burst-ring 700ms ease-out 100ms both",
                          }}
                        />
                        {SPARK_DIRECTIONS.map(([sx, sy]) => (
                          <Star
                            key={`${sx},${sy}`}
                            className="absolute top-1/2 left-1/2 size-3 fill-current text-prizes"
                            style={
                              {
                                "--spark-x": `${sx}px`,
                                "--spark-y": `${sy}px`,
                                animation:
                                  "reveal-spark 650ms cubic-bezier(0.2, 0.7, 0.3, 1) 120ms both",
                              } as React.CSSProperties
                            }
                          />
                        ))}
                      </span>
                    )}
                  </div>
                );
              })}
            </div>
            {/* Edge fades over the strip (Figma "shadow" rails). */}
            <div className="pointer-events-none absolute inset-y-0 left-0 z-10 w-1/5 bg-gradient-to-r from-background-card to-transparent" />
            <div className="pointer-events-none absolute inset-y-0 right-0 z-10 w-1/5 bg-gradient-to-l from-background-card to-transparent" />
          </div>
        )}

        {/* Result — the card springs taller as this section grows open once
            the reel stops (height 0 → auto with a spring). */}
        <motion.div
          className="overflow-hidden"
          initial={false}
          animate={{ height: landed ? "auto" : 0 }}
          transition={
            reduceMotion
              ? { duration: 0 }
              : { type: "spring", stiffness: 240, damping: 22, mass: 0.9 }
          }
        >
          {landed && (
            <div className="flex animate-in flex-col items-center gap-2 px-6 pt-5 duration-300 zoom-in-95 fade-in">
              <p
                aria-live="polite"
                className="text-center font-heading text-2xl leading-normal font-bold text-balance text-foreground"
              >
                <WonSentence
                  sentence={t("prizeReveal.youWon", "You won {{prize}}", {
                    prize: wonPrize,
                  })}
                  prize={wonPrize}
                />
              </p>
              {/* Free-spins games under the prize — the same games line as every prize
                  (PrizeGamesChips), never crammed into the prize cell. */}
              {games && games.length > 0 && (
                <PrizeGamesChips
                  games={games}
                  // No dialog: confirm is the only action this window offers, so the line
                  // spells the names out instead of "N games ›".
                  browsableRemainder={false}
                  className="flex flex-wrap items-center justify-center gap-1.5 px-6 py-1"
                />
              )}
              <button
                ref={confirmRef}
                type="button"
                onClick={onConfirm}
                className="bg-gradient-cta mt-4 flex items-center gap-2 rounded-full px-7 py-4 font-heading text-[17px] leading-none font-bold text-background transition-[filter] hover:brightness-105"
              >
                {/* More wins queued behind this one? Confirming carries straight
                    on to the next reveal rather than closing, so the button says so.
                    Since #3922 "carries on" is a fresh claim — this window unmounts,
                    the surface shows its claiming state, and it remounts on the
                    disclosure — so the caller must route this at `flow.confirm`, not
                    `flow.dismiss`, or the copy lies. */}
                {pendingCount > 1
                  ? t("prizeReveal.nextPrize", "Next Prize")
                  : t("prizeReveal.confirm", "Awesome!")}
                <ArrowRight className="size-4.5 shrink-0" strokeWidth={2.5} />
              </button>
              {/* The way out of a one-at-a-time run, sitting where the run actually
                  happens — so skipping ahead costs one tap from here rather than a
                  detour back to the chooser between every prize. Same secondary
                  treatment and copy as `RevealActions`; no count, because the
                  "You have N Prizes" caption above already states it. */}
              {onRevealAll && (
                <button
                  type="button"
                  onClick={onRevealAll}
                  className="mt-1 flex min-h-11 items-center justify-center font-body text-[13px] font-medium text-foreground-secondary underline underline-offset-[3px]"
                >
                  {t("prizeReveal.revealAll", "Reveal all")}
                </button>
              )}
            </div>
          )}
        </motion.div>
      </div>
    </div>
  );
};
